/**
 * Theme Store
 *
 * Verwaltet das aktuelle Theme (Primary + Neutral Farbe).
 * Später wird das Theme via TenantConfig vom Backend geladen.
 * Aktuell: localStorage für Dev-Testing.
 */

import { signal } from '@preact/signals';

// =============================================================================
// Types
// =============================================================================

export const AVAILABLE_PRIMARY_COLORS = [
  'blue',
  'orange',
  'violet',
  'cyan',
  'pink',
  'bronze',
  'gold',
  'sky',
  'mint',
  'lime',
  'amber',
] as const;
export type PrimaryColor = (typeof AVAILABLE_PRIMARY_COLORS)[number];

export const AVAILABLE_NEUTRAL_COLORS = [
  'slate',
  'gray',
  'mauve',
  'sage',
  'olive',
  'sand',
] as const;
export type NeutralColor = (typeof AVAILABLE_NEUTRAL_COLORS)[number];

// Legacy alias for backwards compatibility
export const AVAILABLE_THEMES = AVAILABLE_PRIMARY_COLORS;
export type Theme = PrimaryColor;

// =============================================================================
// Constants
// =============================================================================

const PRIMARY_STORAGE_KEY = 'timetrack_primary';
const NEUTRAL_STORAGE_KEY = 'timetrack_neutral';
// Legacy key for migration
const LEGACY_THEME_STORAGE_KEY = 'timetrack_theme';

// =============================================================================
// State
// =============================================================================

function getStoredPrimary(): PrimaryColor {
  if (typeof window === 'undefined') return 'blue';

  // Try new key first
  const stored = localStorage.getItem(PRIMARY_STORAGE_KEY);
  if (stored && AVAILABLE_PRIMARY_COLORS.includes(stored as PrimaryColor)) {
    return stored as PrimaryColor;
  }

  // Migrate from legacy key
  const legacy = localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
  if (legacy && AVAILABLE_PRIMARY_COLORS.includes(legacy as PrimaryColor)) {
    localStorage.setItem(PRIMARY_STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
    return legacy as PrimaryColor;
  }

  return 'blue';
}

function getStoredNeutral(): NeutralColor {
  if (typeof window === 'undefined') return 'slate';
  const stored = localStorage.getItem(NEUTRAL_STORAGE_KEY);
  if (stored && AVAILABLE_NEUTRAL_COLORS.includes(stored as NeutralColor)) {
    return stored as NeutralColor;
  }
  return 'slate';
}

export const currentPrimary = signal<PrimaryColor>(getStoredPrimary());
export const currentNeutral = signal<NeutralColor>(getStoredNeutral());

// Legacy alias
export const currentTheme = currentPrimary;

// =============================================================================
// Actions
// =============================================================================

/**
 * Wendet die Primary Color auf das DOM an.
 */
function applyPrimary(primary: PrimaryColor): void {
  if (typeof document === 'undefined') return;

  if (primary === 'blue') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', primary);
  }
}

/**
 * Wendet die Neutral Color auf das DOM an.
 */
function applyNeutral(neutral: NeutralColor): void {
  if (typeof document === 'undefined') return;

  if (neutral === 'slate') {
    document.documentElement.removeAttribute('data-neutral');
  } else {
    document.documentElement.setAttribute('data-neutral', neutral);
  }
}

/**
 * Setzt die Primary Color und persistiert sie.
 */
export function setPrimary(primary: PrimaryColor): void {
  currentPrimary.value = primary;
  applyPrimary(primary);
  localStorage.setItem(PRIMARY_STORAGE_KEY, primary);
}

/**
 * Setzt die Neutral Color und persistiert sie.
 */
export function setNeutral(neutral: NeutralColor): void {
  currentNeutral.value = neutral;
  applyNeutral(neutral);
  localStorage.setItem(NEUTRAL_STORAGE_KEY, neutral);
}

// Legacy alias
export function setTheme(theme: Theme): void {
  setPrimary(theme);
}

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialisiert das Theme beim App-Start.
 * Wird automatisch aufgerufen wenn der Store importiert wird.
 */
export function initTheme(): void {
  applyPrimary(currentPrimary.value);
  applyNeutral(currentNeutral.value);
}

// Auto-init beim Import
if (typeof window !== 'undefined') {
  initTheme();
}
